import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/default-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTRPC } from "@/trpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Icons from "lucide-react";
import { useForm } from "react-hook-form";
import { TPlayerSearch, TShortlistAll, TLonglistAll } from "@/types/trpc.types";
import { toast } from "sonner";
import { TRPCClientError } from "@trpc/client";
import { useNavigate } from "react-router-dom";
import { ROUTES_CONFIG } from "@/config/routes.config";
import { hasRight } from "@/config/rights.config";
import { useAuth } from "@/hooks/use-auth";
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  ExpandedState,
  GroupingState,
  SortingState,
  CellContext,
} from "@tanstack/react-table";

type Player = TPlayerSearch['players'][number];

interface TableState {
  page?: number | string;
  limit?: number | string;
}

type ShortlistItem = TShortlistAll['shortlist'][number];
type LonglistItem = TLonglistAll['longlist'][number];
type ListItem = ShortlistItem | LonglistItem;

interface ColumnConfig {
  key: keyof ListItem | string;
  header: string;
  accessorFn?: (row: ListItem) => string | undefined;
  type?: 'string' | 'boolean';
}

interface ListResponse {
  shortlist?: ShortlistItem[];
  longlist?: LonglistItem[];
  total: number;
  pages: number;
}

const COLUMN_CONFIGS: ColumnConfig[] = [
  { key: 'position', header: 'Pos.', type: 'string' },
  { 
    key: 'name', 
    header: 'Naam', 
    accessorFn: (row) => row.player?.name,
    type: 'string'
  },
  { 
    key: 'club', 
    header: 'Club', 
    accessorFn: (row) => typeof row.player?.club === "object" ? row.player?.club?.name : "-",
    type: 'string'
  },
  { key: 'date_of_birth', 
    header: 'Geboortedatum', 
    accessorFn: (row) => row.player?.dateOfBirth ? row.player?.dateOfBirth : "-",
    type: 'string'
  },
  { key: 'citizenship', 
    header: 'Nationaliteit', 
    accessorFn: (row) => {
      if (Array.isArray(row.player?.citizenship)) {
        return row.player?.citizenship?.join(", ");
      }
      return typeof row.player?.citizenship === "string" ? row.player?.citizenship : undefined;
    },
    type: 'string' 
  },
  { key: 'information_td', header: 'Informatie TD', type: 'string' },
  { key: 'decision', header: 'Besluit', type: 'string' },
  { key: 'advice', header: 'Advies', type: 'string' },
  { key: 'screening', header: 'Screening', type: 'boolean' },
  { key: 'scouting', header: 'Scouting', type: 'boolean' },
  { key: 'data', header: 'Data', type: 'boolean' },
  { key: 'check', header: 'Check', type: 'boolean' },
  { key: 'active', header: 'Actief', type: 'boolean' },
];

const normalizeFilterInput = (filters: TableState) => ({
  ...filters,
  page: filters.page ? Number(filters.page) : undefined,
  limit: filters.limit ? Number(filters.limit) : undefined,
});

interface ListTableProps {
  listType: 'shortlist' | 'longlist';
}

const ListTable = ({ listType }: ListTableProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { trpc } = useTRPC();
  const { getValues, setValue, watch } = useForm<TableState>({
    defaultValues: {
      page: 1
    }
  });
  const filterState = watch();

  const [grouping, setGrouping] = React.useState<GroupingState>(['position']);
  const [expanded, setExpanded] = React.useState<ExpandedState>(true);
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'position', desc: false }
  ]);

  const query = useQuery<ListResponse>({
    queryKey: [listType],
    queryFn: async () => {
      const input = normalizeFilterInput(getValues());
      if (listType === 'shortlist') {
        return await trpc.shortlist.getAll.query(input) as ListResponse;
      } else {
        return await trpc.longlist.getAll.query(input) as ListResponse;
      }
    }
  });

  const columns = React.useMemo<ColumnDef<ListItem>[]>(
    () => {
      const dynamicColumns = COLUMN_CONFIGS.map((config): ColumnDef<ListItem> => {
        if (config.accessorFn) {
          const col: ColumnDef<ListItem> = {
            id: config.key,
            header: config.header,
            accessorFn: config.accessorFn,
          };

          if (config.key === 'name') {
            col.cell = ({ row, getValue }: CellContext<ListItem, string | undefined>) => {
              const item = row.original;
              return (
                <div
                  className="flex items-center gap-2 cursor-pointer group/name"
                  onClick={() => {
                    if (item.player?.id === undefined) return;
                    navigate(ROUTES_CONFIG.APP.PLAYER_DETAIL.$buildPath({ params: { player_id: item.player?.id } }));
                  }}
                >
                  <div className="size-8 aspect-square rounded-full bg-foreground/10">
                    <Avatar>
                      <AvatarImage
                        src={item.player?.imageUrl ?? undefined}
                        alt={item.player?.name ?? undefined}
                      />
                      <AvatarFallback>
                        {item.player?.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <p className="group-hover/name:underline font-medium">{String(getValue() || "-")}</p>
                </div>
              );
            };
          }  else if (config.key === 'club') {
            col.cell = ({ row }: CellContext<ListItem, string | undefined>) => {
              const club = row.original.player?.club;
              if (typeof club !== "object" || !club) {
                return <p>-</p>;
              }
              return (
                <div className="flex items-center gap-2 cursor-pointer hover:underline"
                  onClick={() => {
                    if (!club?.id) return;
                    navigate(ROUTES_CONFIG.APP.CLUB_DETAIL.$buildPath({ params: { club_id: club.id } }));
                  }}
                >
                  {club?.imageUrl && (
                    <div className="size-8 aspect-square rounded-full overflow-hidden flex items-center justify-center bg-foreground/10">
                      <img
                        src={club.imageUrl}
                        alt={club.name ?? "Club logo"}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                  <p>{club?.name}</p>
                </div>
              );
            };
          } 
          else {
            col.cell = ({ getValue }: CellContext<ListItem, string | undefined>) => {
              return <p>{String(getValue() || "-")}</p>;
            };
          }

          return col;
        } else {
          const col: ColumnDef<ListItem> = {
            accessorKey: config.key as keyof ListItem,
            header: config.header,
          };

          if (config.type === 'boolean') {
            col.cell = ({ getValue }) => {
              const value = getValue() as boolean | undefined;
              return <p>{value ? "Ja" : "Nee"}</p>;
            };
          } else {
            col.cell = ({ getValue }) => {
              const value = getValue() as string | undefined;
              return <p>{value || "-"}</p>;
            };
          }

          return col;
        }
      });

      const rightName = listType === 'shortlist' ? 'SHORTLIST.WRITE' : 'LONGLIST.WRITE';
      
      const allColumns: ColumnDef<ListItem>[] = [
        ...(hasRight(user?.role?.name, rightName) ? [{
          id: 'remove',
          header: '',
          cell: ({ row }: CellContext<ListItem, string>) => {
            if (row.getIsGrouped()) return null;
            return <RemoveListButton player={row.original.player as Player} listType={listType} />;
          },
        }] : []),
        ...dynamicColumns
      ];

      return allColumns;
    },
    [user?.role?.name, navigate, listType]
  );

  const getListData = (data: ListResponse | undefined): ListItem[] => {
    if (!data) return [];
    return (listType === 'shortlist' ? data.shortlist : data.longlist) || [];
  };

  const table = useReactTable<ListItem>({
    data: getListData(query?.data),
    columns,
    state: {
      grouping,
      expanded,
      sorting,
    },
    onGroupingChange: setGrouping,
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    autoResetExpanded: false,
    getRowId: (row: ListItem, index: number) => {
      const item = row as ShortlistItem & LonglistItem;
      return item.mongo_player_id || `row-${index}`;
    },
  });

   return (
    <div className="h-full overflow-auto">
      <div className="flex flex-col justify-between gap-4 h-full">
        <div className="bg-foreground/10 rounded-md border p-2 scroll-pt-0 overflow-auto h-full relative">
          <Table className='overflowclip w-full relative' divClassname='overflow-y-scroll max-h-[100%]'>
            <TableHeader className="sticky top-0 w-full border-b bg-sidebar/50 backdrop-blur-md z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={`hover:bg-foreground/10 group/row ${
                    row.getIsGrouped() ? 'bg-foreground/5 font-semibold' : ''
                  }`}
                >
                  {row.getIsGrouped() ? (
                    <TableCell colSpan={columns.length}>
                      <div className="flex items-center gap-2 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={row.getToggleExpandedHandler()}
                        >
                          {row.getIsExpanded() ? (
                            <Icons.ChevronDown className="w-4 h-4" />
                          ) : (
                            <Icons.ChevronRight className="w-4 h-4" />
                          )}
                        </Button>
                        <span className="uppercase">{String(row.groupingValue ?? '-')}</span>
                        <span className="text-muted-foreground text-sm">
                          ({row.subRows.length})
                        </span>
                      </div>
                    </TableCell>
                  ) : (
                    row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-medium text-foreground/50">
            {query?.data?.total ?? 0} rijen gevonden
          </p>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              onClick={() => setValue("page", 1)}
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                setValue("page", Math.max(1, Number(filterState.page) - 1))
              }
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronLeft className="w-4 h-4" />
            </Button>
            <Input 
              value={filterState.page}
              onChange={(e) => {
                setValue('page', e.currentTarget.valueAsNumber);
              }}
              onBlur={() => {
                query.refetch();
              }}
              type="number"
              min={1}
              max={query?.data?.pages || 1}
            />
            <Button
              variant="outline"
              onClick={() =>
                setValue(
                  "page",
                  Math.min(
                    Number(query?.data?.pages || 1),
                    Number(filterState.page) + 1
                  )
                )
              }
              disabled={Number(filterState.page) >= Number(query?.data?.pages || 1)}
            >
              <Icons.ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setValue("page", query?.data?.pages || 1)}
              disabled={Number(filterState.page) >= Number(query?.data?.pages || 1)}
            >
              <Icons.ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface RemoveListButtonProps {
  player: Player;
  listType: 'shortlist' | 'longlist';
}

const RemoveListButton = ({ player, listType }: RemoveListButtonProps) => {
  const queryClient = useQueryClient();
  const { trpc } = useTRPC();

  const { mutate: removeFromList } = useMutation({
    mutationFn: async (player_id: string) => {
      if (listType === 'shortlist') {
        await trpc.shortlist.remove.mutate({ player_id });
      } else {
        await trpc.longlist.remove.mutate({ player_id });
      }
    },
    onMutate: async (player_id: string) => {
      await queryClient.cancelQueries({ queryKey: [listType] });
      const previous = queryClient.getQueryData<ListResponse>([listType]);

      queryClient.setQueryData<ListResponse>([listType], (old) => {
        if (!old) return old;

        const currentList = (listType === 'shortlist' ? old.shortlist : old.longlist) || [];
        const updatedList = currentList.filter((item: ListItem) => item.mongo_player_id !== player_id);

        return {
          ...old,
          [listType]: updatedList,
          total: old.total - 1,
        };
      });

      return { previous };
    },
    onSuccess: () => {
      toast.success(`Speler verwijderd uit de ${listType === 'shortlist' ? 'shortlist' : 'longlist'}`);
      queryClient.refetchQueries({ queryKey: [listType] });
    },
    onError: (err, _variables: string, context?: { previous?: ListResponse }) => {
      if (context?.previous) {
        queryClient.setQueryData([listType], context.previous);
      }

      if (err instanceof TRPCClientError) {
        toast.error(err.message);
      } else if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error("Er is een onbekende fout opgetreden.");
      }
    }
  });

  function remove() {
    if (!player.id) return;
    removeFromList(player.id.toString());
  }

  return (
    <div className="relative flex items-center justify-center">
      <Button onClick={() => { remove(); }} variant="bright" shape="pill" size="icon" className="group-hover/row:opacity-100 opacity-0 z-10 relative">
        <Icons.UserMinus className="w-4 h-4" />
      </Button>
      <Icons.CheckIcon className="w-4 h-4 text-bright absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-100 group-hover/row:opacity-0" />
    </div>
  );
};

export default ListTable;